
import React, { useMemo, useState, useEffect } from 'react';
import WorkflowEditor from './components/editors/WorkflowEditor';
import WorkflowSimulator from './components/editors/WorkflowSimulator';
import { EditorProvider } from './contexts/EditorContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NODE_CATEGORIES } from './constants';
import { defaultComponentRegistry } from './components/registry/componentRegistry';
import { getNodeTypes, getPropertyRegistry, registerNodes, NodeRegistration } from './utils/nodeRegistry';
import { directoryService } from './services/mockDirectoryService';
import { DmnService, mockDmnFiles, DmnDefinition } from './services/mockDmnService';
import { useWorkflowEditor } from './hooks/useWorkflowEditor';
import { resolveTargetHandles } from './utils/resolveTargetHandles';
import { useWorkflowSimulation } from './hooks/useWorkflowSimulation';
import { useAiCapability } from './hooks/useAiCapability';
import { EditorAiCapability } from './utils/aiCapability';
import { ProcessDefinition, AIService, CallableProcess, WorkflowProject, NamedRuleSet, MonitorMode, NotificationCatalogService } from './types';
import { AutoSavePayload } from './hooks/useAutoSave';

export type DirectoryService = typeof directoryService;

export interface ControllerProps {
  initialData?: ProcessDefinition[];
  initialRuleSets?: NamedRuleSet[];
  onBack?: () => void;
  onSave?: (data: WorkflowProject) => Promise<void>;
  onSaveAndQuit?: (data: WorkflowProject) => Promise<void>;
  onAutoSave?: (data: AutoSavePayload) => void;
  isLoading?: boolean;
  // Heterogeneous component registry — each component has its own props shape,
  // so ComponentType<any> is the standard idiom for a loose registry.
   
  customComponents?: Record<string, React.ComponentType<any>>;
  customNodes?: Record<string, NodeRegistration>;
  aiService?: AIService;
  directoryService?: DirectoryService;
  dmnDefinitions?: DmnDefinition[];
  callableProcesses?: CallableProcess[];
  notificationService?: NotificationCatalogService;
  theme?: "light" | "dark";
  /** When set, the editor runs read-only as a live monitor (badges + node clicks, no editing chrome). */
  monitor?: MonitorMode;
  /**
   * Optional, additive. Called once with a granular, validating
   * {@link EditorAiCapability} the host's AI assistant can invoke against the
   * live model, and with `null` on unmount. Does not affect any other prop or
   * behaviour.
   */
  onAiCapabilityReady?: (cap: EditorAiCapability | null) => void;
}

const ControllerContent: React.FC<Omit<ControllerProps, 'theme'>> = ({
  initialData,
  initialRuleSets,
  onBack,
  onSave,
  onSaveAndQuit,
  onAutoSave,
  isLoading: externalLoading = false,
  customComponents = {},
  customNodes,
  aiService,
  directoryService: externalDirectoryService,
  dmnDefinitions,
  callableProcesses,
  notificationService,
  monitor,
  onAiCapabilityReady
}) => {
  const [fallbackAi, setFallbackAi] = useState<AIService | null>(null);
  const [customNodesVersion, setCustomNodesVersion] = useState(0);

  useEffect(() => {
    if (customNodes) {
      registerNodes(customNodes);
      setCustomNodesVersion(v => v + 1);
    }
  }, [customNodes]);

  useEffect(() => {
    if (!aiService && !fallbackAi) {
      import('./services/geminiService').then(m => {
        setFallbackAi(m.geminiService);
      });
    }
  }, [aiService, fallbackAi]);

  const editor = useWorkflowEditor({
    initialData,
    initialRuleSets,
    onAutoSave
  });

  // Adapters: route AI view/selection tools through the editor's REAL
  // selection/edge/clear primitives (the raw setters aren't all exported).
  const aiSelectEdge = useMemo(
    () => (edgeId: string) => {
      const edge = editor.edges.find((e) => e.id === edgeId);
      // onEdgeClick ignores its event arg; pass a benign placeholder.
      if (edge) editor.onEdgeClick({} as React.MouseEvent, edge);
    },
    [editor.edges, editor.onEdgeClick]
  );
  const aiClearSelection = useMemo(
    () => () => editor.onPaneClick(),
    [editor.onPaneClick]
  );

  // Expose a granular, validating AI capability to the host (additive/optional).
  // Routed through the editor's real mutation + validation logic, plus real
  // viewport/selection/process operations for the navigation tools.
  useAiCapability({
    nodes: editor.nodes,
    edges: editor.edges,
    setNodes: editor.setNodes,
    setEdges: editor.setEdges,
    processes: editor.processes,
    activeProcessId: editor.activeProcessId,
    swimlanes: editor.swimlanes,
    ruleSets: editor.ruleSets,
    selectedNodeId: editor.selectedNode?.id ?? null,
    selectedEdgeId: editor.selectedEdge?.id ?? null,
    selectedSwimlaneId: editor.selectedSwimlaneId ?? null,
    modalOpen: editor.isModalOpen,
    modalNodeId: editor.isModalOpen ? editor.selectedNode?.id ?? null : null,
    modalTabId: editor.isModalOpen ? editor.activeTabId ?? null : null,
    fitView: editor.fitView,
    focusNode: editor.focusNode,
    focusAndSelectNode: editor.focusAndSelectNode,
    selectNode: editor.focusAndSelectNode,
    selectEdge: aiSelectEdge,
    selectSwimlane: editor.selectSwimlane,
    clearSelection: aiClearSelection,
    openModalAtTab: editor.openModalAtTab,
    switchProcess: editor.handleSelectProcess,
    onAiCapabilityReady,
  });

  const resolvedEdges = useMemo(
    () => resolveTargetHandles(editor.nodes, editor.edges),
    [editor.nodes, editor.edges]
  );

  // Annotate each node with which of its target handles are actually in use
  // so CustomNode can hide the unused one. Stable-reference when unchanged
  // so React Flow doesn't churn.
  const decoratedNodes = useMemo(() => {
    const usage = new Map<string, { left: boolean; top: boolean }>();
    for (const edge of resolvedEdges) {
      if (edge.targetHandle !== 't-left' && edge.targetHandle !== 't-top') continue;
      const u = usage.get(edge.target) || { left: false, top: false };
      if (edge.targetHandle === 't-left') u.left = true;
      else u.top = true;
      usage.set(edge.target, u);
    }
    const counts = monitor?.nodeCounts;
    const selectedId = monitor?.selectedNodeId ?? null;
    let changed = false;
    const next = editor.nodes.map((n) => {
      const u = usage.get(n.id) || { left: false, top: false };
      const prevUsage = n.data?.targetHandleUsage;
      const usageSame = prevUsage && prevUsage.left === u.left && prevUsage.top === u.top;

      // Monitor overlay: badge count + selection highlight keyed by engine node Id.
      const monitorOn = !!monitor;
      const engineId = n.data?.engineNodeId;
      const count = monitor && engineId != null ? counts?.[engineId] ?? 0 : undefined;
      const isSelected = monitor && engineId != null ? selectedId === engineId : undefined;
      const monitorSame =
        n.data?.monitorMode === monitorOn &&
        n.data?.monitorCount === count &&
        n.data?.monitorSelected === isSelected;

      if (usageSame && monitorSame) return n;
      changed = true;
      return { ...n, data: { ...n.data, targetHandleUsage: u, monitorMode: monitorOn, monitorCount: count, monitorSelected: isSelected } };
    });
    return changed ? next : editor.nodes;
  }, [editor.nodes, resolvedEdges, monitor]);

  const simulator = useWorkflowSimulation({
      nodes: editor.nodes,
      setNodes: editor.setNodes,
      edges: editor.edges,
      fitView: editor.fitView,
      focusNode: editor.focusNode
  });

  // Initialize DMN Service with props or default
  const dmnServiceInstance = useMemo(() => {
      return new DmnService(dmnDefinitions || mockDmnFiles);
  }, [dmnDefinitions]);

  const config = useMemo(() => ({
    nodeTypes: getNodeTypes(),
    nodeCategories: NODE_CATEGORIES,
    propertyRegistry: getPropertyRegistry(),
    componentRegistry: { ...defaultComponentRegistry, ...customComponents },
    services: {
      ai: aiService || fallbackAi || {
          generateWorkflowCompletion: async () => "Initializing AI...",
          evaluateCondition: async () => true
      },
      directory: externalDirectoryService || directoryService,
      dmn: dmnServiceInstance,
      callableProcesses,
      notifications: notificationService
    },
    actions: {
        openModal: editor.openModalAtTab,
        activeNodeId: editor.selectedNode?.id,
    },
    errorRulesets: {
        list: editor.ruleSets,
        create: editor.createRuleSet,
        update: editor.updateRuleSet,
        remove: editor.removeRuleSet,
        usageCount: editor.ruleSetUsageCount,
    }
  }), [customComponents, customNodesVersion, editor.openModalAtTab, editor.selectedNode?.id, aiService, fallbackAi, dmnServiceInstance, externalDirectoryService, callableProcesses, notificationService, editor.ruleSets, editor.createRuleSet, editor.updateRuleSet, editor.removeRuleSet, editor.ruleSetUsageCount]);

  // Monitor mode: clicking a node reports its engine node Id to the host instead of
  // selecting it for editing. Editing-only interactions are disabled.
  const onMonitorNodeClick = useMemo(
    () => (_: React.MouseEvent, node: { data?: { engineNodeId?: number } }) => {
      const engineId = node.data?.engineNodeId;
      if (engineId != null) monitor?.onNodeSelect?.(engineId);
    },
    [monitor]
  );

  const onNodeClick = monitor
    ? onMonitorNodeClick
    : simulator.simulationState.isRunning ? undefined : editor.onNodeClick;
  const onEdgeClick = monitor || simulator.simulationState.isRunning ? undefined : editor.onEdgeClick;
  const onPaneClick = monitor || simulator.simulationState.isRunning ? undefined : editor.onPaneClick;
  const onNodeDoubleClick = monitor || simulator.simulationState.isRunning ? undefined : editor.onNodeDoubleClick;
  const onNodeContextMenu = monitor || simulator.simulationState.isRunning ? undefined : editor.onNodeContextMenu;

  const handleExternalSave = async () => {
      if (onSave) {
          editor.setIsLoading(true);
          try {
              await onSave(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleSaveAndQuitClick = async () => {
      if (onSaveAndQuit) {
          editor.setIsLoading(true);
          try {
              await onSaveAndQuit(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleExport = (opts?: { bypassValidation?: boolean }) => {
      editor.handleSaveProject(opts);
  };

  const isGlobalLoading = editor.isLoading || externalLoading;

  return (
    <EditorProvider config={config}>
      {simulator.simulationState.isRunning ? (
        <WorkflowSimulator
          nodes={editor.nodes}
          edges={editor.edges}
          
          simulationState={simulator.simulationState}
          onToggleSimulation={simulator.stopSimulation}
          onResetSimulation={simulator.resetSimulation}
          onSimulationAction={simulator.handleSimulationAction}
          suggestedPath={simulator.suggestedPath}
          
          activeProcessId={editor.activeProcessId}
          processes={editor.processes}
          onSelectProcess={editor.handleSelectProcess}
          onCreateProcess={editor.handleCreateProcess}
          onDeleteProcess={editor.handleDeleteProcess}
          onSaveProject={handleExport}
          onLoadProject={editor.handleLoadProject}
          onBack={onBack}
          onSave={onSave ? handleExternalSave : undefined}
          onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}
        />
      ) : (
        <WorkflowEditor
            nodes={decoratedNodes}
            edges={resolvedEdges}
            onNodesChange={editor.onNodesChange}
            onEdgesChange={editor.onEdgesChange}
            onConnect={editor.onConnect}
            onConnectStart={editor.onConnectStart}
            onConnectEnd={editor.onConnectEnd}
            onDrop={editor.onDrop}
            onDragOver={editor.onDragOver}
            
            simulationState={simulator.simulationState}
            onToggleSimulation={simulator.startSimulation}
            onResetSimulation={simulator.resetSimulation}
            
            activeProcessId={editor.activeProcessId}
            processes={editor.processes}
            onSelectProcess={editor.handleSelectProcess}
            onCreateProcess={editor.handleCreateProcess}
            onCreateSubProcess={editor.handleCreateSubProcess}
            onDeleteProcess={editor.handleDeleteProcess}
            onUpdateProcess={editor.handleUpdateProcess}
            
            onSaveProject={handleExport}
            onLoadProject={editor.handleLoadProject}
            onAutoLayout={editor.handleAutoLayout}
            
            onBack={onBack}
            onSave={onSave ? handleExternalSave : undefined}
            onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}

            onNodeClick={onNodeClick!}
            onEdgeClick={onEdgeClick!}
            onNodeDoubleClick={onNodeDoubleClick!}
            onNodeContextMenu={onNodeContextMenu!}
            onNodeDragStop={editor.onNodeDragStop}
            onPaneClick={onPaneClick!}
            swimlanes={editor.swimlanes}
            selectedSwimlaneId={editor.selectedSwimlaneId}
            selectSwimlane={editor.selectSwimlane}
            addSwimlane={editor.addSwimlane}
            renameSwimlane={editor.renameSwimlane}
            moveSwimlane={editor.moveSwimlane}
            moveSwimlaneBy={editor.moveSwimlaneBy}
            resizeSwimlane={editor.resizeSwimlane}
            setSwimlaneColor={editor.setSwimlaneColor}
            removeSwimlane={editor.removeSwimlane}
            onSelectionChange={editor.onSelectionChange}
            selectedNode={editor.selectedNode}
            selectedEdge={editor.selectedEdge}
            updateNodeData={editor.updateNodeData}
            updateEdgeData={editor.updateEdgeData}
            onRenameNode={editor.renameNode}
            isModalOpen={editor.isModalOpen}
            setIsModalOpen={editor.setIsModalOpen}
            activeTabId={editor.activeTabId}
            setActiveTabId={editor.setActiveTabId}
            isDeployModalOpen={editor.isDeployModalOpen}
            setIsDeployModalOpen={editor.setIsDeployModalOpen}
            
            monitorMode={!!monitor}
            isLoading={isGlobalLoading}
            autoSaveState={editor.autoSaveState}
            variableContext={editor.variableContext}
            validationIssues={editor.validation.issues}
            validationErrors={editor.validation.errors}
            validationWarnings={editor.validation.warnings}
            processIssueCounts={editor.validation.processCounts}
            onFocusNode={editor.focusAndSelectNode}
        />
      )}
    </EditorProvider>
  );
};

const Controller: React.FC<ControllerProps> = ({ theme, ...rest }) => {
    return (
        <ThemeProvider forcedTheme={theme}>
            <ControllerContent {...rest} />
        </ThemeProvider>
    );
};

export default Controller;
